import { CONFIG, promoteCountForGroup, tierName } from '@h2h/shared';
import { TierBadge } from '../ui/bits';
import { ArrowRight, ChevronUp, Trophy } from '../ui/icons';
import { cap, ordinalSuffix } from '../../lib/format';
import { cn } from '../../lib/cn';

export function RoundResultsHero({
  outcome,
  outcomeBadge,
  rank,
  divisionSize,
  fromTierIndex,
  toTierIndex,
  won = false
}: {
  outcome: 'PROMOTED' | 'STAYED' | 'RELEGATED';
  outcomeBadge: string;
  rank: number;
  divisionSize: number;
  fromTierIndex: number;
  toTierIndex: number;
  /** Finished 1st in the group. Celebrated as a "round won" — the headline reward at
   *  the top tier, where promotion isn't possible, and a moment for any group winner
   *  who held (a promoting winner gets the promotion treatment instead). */
  won?: boolean;
}) {
  const promoted = outcome === 'PROMOTED';
  const relegated = outcome === 'RELEGATED';
  // Champion treatment only when winning didn't already trigger a promotion (top-tier
  // hold-wins, or a winner in a group too small to promote) — otherwise green wins.
  const champion = won && !promoted && !relegated;
  const held = !promoted && !relegated && !champion;
  const promote = promoteCountForGroup(divisionSize);
  const offPromotion = held && promote > 0 && rank > promote ? rank - promote : 0;
  const outcomeLabel = promoted ? 'PROMOTED' : relegated ? 'RELEGATED' : champion ? 'ROUND WON' : 'HELD';

  return (
    <div className="relative overflow-hidden rounded-[30px] bg-gradient-to-br from-[#1C1C24] to-[#0A0A0E] px-[22px] pb-6 pt-[26px] shadow-[0_24px_46px_-18px_rgba(0,0,0,0.6)]">
      <span
        className={cn(
          'pointer-events-none absolute left-1/2 top-[-90px] h-[240px] w-[300px] -translate-x-1/2 rounded-full',
          promoted && 'bg-[radial-gradient(circle,rgba(36,197,106,0.32),transparent_68%)]',
          relegated && 'bg-[radial-gradient(circle,rgba(210,84,79,0.28),transparent_68%)]',
          champion && 'bg-[radial-gradient(circle,rgba(227,168,43,0.34),transparent_68%)]',
          held && 'bg-[radial-gradient(circle,rgba(255,255,255,0.12),transparent_68%)]'
        )}
      />

      <div className="relative text-center">
        <div
          className={cn(
            'inline-flex items-center gap-[7px] rounded-full border px-[13px] py-[6px]',
            promoted && 'border-accent/35 bg-accent/15',
            relegated && 'border-danger/35 bg-danger/10',
            champion && 'border-[#E3A82B]/40 bg-[#E3A82B]/15',
            held && 'border-white/15 bg-white/10'
          )}
        >
          {promoted ? (
            <ChevronUp width={13} height={13} strokeWidth={3} className="text-accent" />
          ) : champion ? (
            <Trophy width={13} height={13} strokeWidth={2.6} className="text-[#E8C25E]" />
          ) : null}
          <span
            className={cn(
              'text-[11px] font-extrabold tracking-wide',
              promoted && 'text-accent',
              relegated && 'text-danger',
              champion && 'text-[#E8C25E]',
              held && 'text-ink3'
            )}
          >
            {outcomeBadge}
          </span>
        </div>

        <div
          className={cn(
            'mt-[18px] text-[46px] font-extrabold leading-none tracking-title',
            promoted && 'text-accent [text-shadow:0_0_36px_rgba(36,197,106,0.45)]',
            relegated && 'text-danger',
            champion && 'text-[#E8C25E] [text-shadow:0_0_36px_rgba(227,168,43,0.45)]',
            held && 'text-white'
          )}
        >
          {outcomeLabel}
        </div>
        <div className="mt-[10px] text-[14px] font-semibold text-ink4">
          You finished{' '}
          <span className="font-extrabold text-white">
            {rank}
            {ordinalSuffix(rank)}
          </span>{' '}
          of {divisionSize}
        </div>
        {offPromotion > 0 && (
          <div className="mt-[6px] text-[12px] font-semibold text-ink3">
            {offPromotion === 1 ? 'Just 1 spot off promotion' : `${offPromotion} spots off promotion`}
          </div>
        )}

        {fromTierIndex !== toTierIndex && (
          <div className="mt-5 flex items-center justify-center gap-3">
            <div className="flex flex-col items-center gap-[7px] opacity-55">
              <TierBadge tierIndex={fromTierIndex} size={46} />
              <span className="text-[10px] font-bold tracking-wide text-ink2">
                {cap(tierName(fromTierIndex))}
              </span>
            </div>
            <ArrowRight width={22} height={22} strokeWidth={2.6} className="text-accent" />
            <div className="flex flex-col items-center gap-[7px]">
              <TierBadge tierIndex={toTierIndex} size={54} />
              <span className="text-[11px] font-extrabold tracking-wide text-white">
                {cap(tierName(toTierIndex))}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function TierLadder({
  currentTierIndex,
  tiersGained,
  label = 'Tier ladder'
}: {
  currentTierIndex: number;
  tiersGained?: number;
  label?: string;
}) {
  const heights = [16, 26, 38, 52, 66, 82];

  return (
    <div className="rounded-[22px] border border-black/[0.08] bg-card p-[18px] shadow-[0_4px_20px_-8px_rgba(20,20,50,0.14),0_1px_3px_rgba(20,20,50,0.06)]">
      <div className="mb-[18px] flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-wide text-ink2">{label}</span>
        {tiersGained != null && tiersGained !== 0 && (
          <span className="text-[12px] font-bold text-accent-ink">
            ▲ {tiersGained > 0 ? `Moved up ${tiersGained}` : `Moved down ${Math.abs(tiersGained)}`}
          </span>
        )}
      </div>
      <div className="flex items-end justify-between gap-[6px]">
        {CONFIG.TIERS.map((_, i) => {
          const active = i === currentTierIndex;
          return (
            <div key={i} className="relative flex-1 text-center">
              {active && (
                <div className="absolute -top-4 inset-x-0 text-[11px] font-extrabold text-accent-ink">
                  YOU
                </div>
              )}
              <div
                style={{ height: heights[i] }}
                className={cn(
                  'rounded-md',
                  active
                    ? 'border-[1.5px] border-accent bg-gradient-to-b from-[#E6ECF5] to-[#9FB2CC] shadow-[0_0_20px_rgba(36,197,106,0.4)]'
                    : i < currentTierIndex
                      ? 'bg-gradient-to-b from-gold-top to-gold-bottom opacity-50'
                      : 'bg-fill2'
                )}
              />
              <div
                className={cn(
                  'mt-[6px] text-[9px] font-bold',
                  active ? 'font-extrabold text-[#3C4C66]' : 'text-ink3'
                )}
              >
                {CONFIG.TIERS[i].slice(0, 4)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
